export type EntityPrefillType = 'person' | 'team' | 'skill' | 'shift' | 'plan';

// ---------------------------------------------------------------------------
// Person prefill
// ---------------------------------------------------------------------------

export type PersonPrefillSex = 'male' | 'female' | 'other' | 'unspecified';
export type PersonPrefillContactType = 'email' | 'phone';

export interface PersonPrefillAddress {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

export interface PersonPrefillContact {
  type: PersonPrefillContactType;
  value: string;
  label?: string;
}

export interface PersonPrefillTeam {
  /** Team name as provided in the user message — matched against available teams by name */
  name: string;
  /** Optional: start date (ISO YYYY-MM-DD) */
  startDate?: string;
  /** Optional: end date (ISO YYYY-MM-DD) */
  endDate?: string;
}

export type PersonPrefillLocationType = 'primary' | 'secondary' | 'home_office';

export interface PersonPrefillLocation {
  /** Location name as provided in the user message — matched against available locations by name */
  name: string;
  /** Assignment type: primary, secondary, or home_office (defaults to 'primary') */
  type?: PersonPrefillLocationType;
  /** Optional: start date (ISO YYYY-MM-DD) */
  startDate?: string;
  /** Optional: end date (ISO YYYY-MM-DD) */
  endDate?: string;
}

export interface PersonPrefill {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  sex?: PersonPrefillSex;
  nationality?: string;
  profilePicture?: string;
  address?: PersonPrefillAddress;
  contacts?: PersonPrefillContact[];
  /** Team memberships to pre-assign — matched by team name */
  teams?: PersonPrefillTeam[];
  /** Location assignments to pre-assign — matched by location name */
  locations?: PersonPrefillLocation[];
}

// ---------------------------------------------------------------------------
// Team prefill
// ---------------------------------------------------------------------------

export interface TeamPrefill {
  name?: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Skill prefill
// ---------------------------------------------------------------------------

export interface SkillPrefill {
  name?: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Shift definition prefill
// ---------------------------------------------------------------------------

export type ShiftPrefillStatus = 'active' | 'inactive';

export interface ShiftPrefill {
  name?: string;
  status?: ShiftPrefillStatus;
  /** Break duration in minutes */
  breakMinutes?: number;
  /** Whether shift overlap is allowed */
  allowOverlap?: boolean;
}

// ---------------------------------------------------------------------------
// Plan prefill
// ---------------------------------------------------------------------------

export interface PlanPrefill {
  name?: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Discriminated union
// ---------------------------------------------------------------------------

export type EntityPrefill =
  | { type: 'person'; data: PersonPrefill; displayText?: string }
  | { type: 'team'; data: TeamPrefill; displayText?: string }
  | { type: 'skill'; data: SkillPrefill; displayText?: string }
  | { type: 'shift'; data: ShiftPrefill; displayText?: string }
  | { type: 'plan'; data: PlanPrefill; displayText?: string };
